import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  UsersIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  UserIcon,
  KeyIcon,
  
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  
} from '@heroicons/react/24/outline'
import ModalUsuario from './ModalUsuario'
import { resolverPresentacionErrorApi } from '../../../services/apiError'
import { usuariosService, Usuario, EstadisticasUsuarios } from '../../../services/usuariosService'
import { usePermisos } from '../../../ganchos/usePermisos'
import { useToast } from '../../../ganchos/interfaz-usuario/usarNotificaciones'

// Interfaces para tipado
interface FiltrosUsuariosLocal {
  busqueda: string
  rol: string
  estado: string
  suscripcion: string
  fechaDesde: string
  fechaHasta: string
}

const PaginaGestionUsuarios: React.FC = () => {
  const { tienePermiso } = usePermisos()
  const { showToast } = useToast()
  const [usuarios, setUsuarios] = useState<Usuario[]>([])
  const [usuariosFiltrados, setUsuariosFiltrados] = useState<Usuario[]>([])
  const [estadisticasUsuarios, setEstadisticasUsuarios] = useState<EstadisticasUsuarios | null>(null)
  const [cargando, setCargando] = useState(true)
  const [filtros, setFiltros] = useState<FiltrosUsuariosLocal>({
    busqueda: '',
    rol: '',
    estado: '',
    suscripcion: '',
    fechaDesde: '',
    fechaHasta: ''
  })
  const [ordenamiento, setOrdenamiento] = useState<{campo: string, direccion: 'asc' | 'desc'}>({
    campo: 'fechaRegistro',
    direccion: 'desc'
  })
  const [paginaActual, setPaginaActual] = useState(1)
  const [usuariosPorPagina] = useState(10)
  const [mostrarFiltros, setMostrarFiltros] = useState(false)
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState<Usuario | null>(null)
  const [modalVisible, setModalVisible] = useState(false)
  const [tipoModal, setTipoModal] = useState<'ver' | 'editar' | 'crear' | 'eliminar'>('ver')
  const [passwordTemporalInfo, setPasswordTemporalInfo] = useState<{
    usuario: string
    email: string
    password: string
    sesionesInvalidadas: boolean
  } | null>(null)

  // Cargar datos
  useEffect(() => {
    const cargarUsuarios = async () => {
      setCargando(true)
      try {
        const [usuariosData, estadisticasData] = await Promise.all([
          usuariosService.obtenerUsuarios(),
          usuariosService.obtenerEstadisticas(),
        ])
        setUsuarios(usuariosData)
        setUsuariosFiltrados(usuariosData)
        setEstadisticasUsuarios(estadisticasData)
      } catch (error) {
        console.error('Error al cargar usuarios:', error)
        const presentacion = resolverPresentacionErrorApi(error, {
          tituloFallback: 'Error al cargar usuarios',
          mensajeFallback: 'No se pudo obtener la lista real de usuarios desde la base de datos.',
        })
        showToast({
          type: 'error',
          title: presentacion.title,
          message: presentacion.message,
        })
        setUsuarios([])
        setUsuariosFiltrados([])
        setEstadisticasUsuarios(null)
      } finally {
        setCargando(false)
      }
    }

    cargarUsuarios()
  }, [])

  // Aplicar filtros
  useEffect(() => {
    let usuariosFiltrados = [...usuarios]

    // Filtro de búsqueda
    if (filtros.busqueda) {
      usuariosFiltrados = usuariosFiltrados.filter(usuario =>
        usuario.nombre.toLowerCase().includes(filtros.busqueda.toLowerCase()) ||
        (usuario.apellido || '').toLowerCase().includes(filtros.busqueda.toLowerCase()) ||
        usuario.email.toLowerCase().includes(filtros.busqueda.toLowerCase()) ||
        (usuario.empresa || '').toLowerCase().includes(filtros.busqueda.toLowerCase())
      )
    }

    // Filtro por rol
    if (filtros.rol) {
      usuariosFiltrados = usuariosFiltrados.filter(usuario => {
        if (filtros.rol.startsWith('global:')) {
          return usuario.rol_global === filtros.rol.replace('global:', '')
        }
        if (filtros.rol.startsWith('institucion:')) {
          return usuario.tipo_organizacion === 'institucion' && usuario.rol_organizacion === filtros.rol.replace('institucion:', '')
        }
        if (filtros.rol.startsWith('empresa:')) {
          return usuario.tipo_organizacion === 'empresa' && usuario.rol_organizacion === filtros.rol.replace('empresa:', '')
        }
        return usuario.rol === filtros.rol
      })
    }

    // Filtro por estado
    if (filtros.estado) {
      usuariosFiltrados = usuariosFiltrados.filter(usuario => usuario.estado === filtros.estado)
    }

    // Filtro por suscripción
    if (filtros.suscripcion) {
      usuariosFiltrados = usuariosFiltrados.filter(usuario => usuario.suscripcion === filtros.suscripcion)
    }

    // Ordenamiento
    usuariosFiltrados.sort((a, b) => {
      const valorA = a[ordenamiento.campo as keyof Usuario] as string
      const valorB = b[ordenamiento.campo as keyof Usuario] as string
      
      if (ordenamiento.direccion === 'asc') {
        return valorA.localeCompare(valorB)
      } else {
        return valorB.localeCompare(valorA)
      }
    })

    setUsuariosFiltrados(usuariosFiltrados)
    setPaginaActual(1)
  }, [filtros, ordenamiento, usuarios])

  // Paginación
  const indiceInicio = (paginaActual - 1) * usuariosPorPagina
  const indiceFin = indiceInicio + usuariosPorPagina
  const usuariosPaginados = usuariosFiltrados.slice(indiceInicio, indiceFin)
  const totalPaginas = Math.ceil(usuariosFiltrados.length / usuariosPorPagina)

  const obtenerColorEstado = (estado: string) => {
    switch (estado) {
      case 'activo':
        return 'bg-green-100 text-green-800'
      case 'inactivo':
        return 'bg-gray-100 text-gray-800'
      case 'suspendido':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const obtenerColorRol = (rol: string) => {
    switch (rol) {
      case 'superadmin':
        return 'bg-fuchsia-100 text-fuchsia-800'
      case 'admin':
        return 'bg-purple-100 text-purple-800'
      case 'moderador':
        return 'bg-blue-100 text-blue-800'
      case 'usuario':
        return 'bg-gray-100 text-gray-800'
      case 'institucion_admin':
        return 'bg-red-100 text-red-800'
      case 'institucion_supervisor':
        return 'bg-orange-100 text-orange-800'
      case 'institucion_jefe':
        return 'bg-indigo-100 text-indigo-800'
      case 'institucion_usuario':
        return 'bg-pink-100 text-pink-800'
      case 'institucion_alumno':
        return 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-300'
      case 'empresa_admin':
        return 'bg-cyan-100 text-cyan-800'
      case 'empresa_supervisor':
        return 'bg-sky-100 text-sky-800'
      case 'empresa_contador':
        return 'bg-emerald-100 text-emerald-800'
      case 'empresa_usuario':
        return 'bg-slate-100 text-slate-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const obtenerColorSuscripcion = (suscripcion?: string) => {
    switch (suscripcion) {
      case 'basica':
        return 'bg-yellow-100 text-yellow-800'
      case 'premium':
        return 'bg-blue-100 text-blue-800'
      case 'empresarial':
        return 'bg-purple-100 text-purple-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const totalGlobales = estadisticasUsuarios?.porTipoOrganizacion?.global ?? usuarios.filter(u => !u.tipo_organizacion).length
  const totalInstitucionales = estadisticasUsuarios?.porTipoOrganizacion?.institucion ?? usuarios.filter(u => u.tipo_organizacion === 'institucion').length
  const totalEmpresariales = estadisticasUsuarios?.porTipoOrganizacion?.empresa ?? usuarios.filter(u => u.tipo_organizacion === 'empresa').length
  const totalAdminsGlobales = (estadisticasUsuarios?.porRolGlobal?.admin ?? 0) + (estadisticasUsuarios?.porRolGlobal?.superadmin ?? 0)

  const manejarOrdenamiento = (campo: string) => {
    setOrdenamiento(prev => ({
      campo,
      direccion: prev.campo === campo && prev.direccion === 'asc' ? 'desc' : 'asc'
    }))
  }

  const abrirModal = (tipo: typeof tipoModal, usuario?: Usuario) => {
    setTipoModal(tipo)
    setUsuarioSeleccionado(usuario || null)
    setModalVisible(true)
  }

  const cerrarModal = () => {
    setModalVisible(false)
    setUsuarioSeleccionado(null)
  }

  const manejarGuardarUsuario = async (usuario: Usuario) => {
    try {
      const rolPayload = usuariosService.mapearRolFormularioAPayload(usuario)
      const idInstitucion = usuario.tipo_organizacion === 'institucion' ? usuario.id_institucion : undefined
      const idOrganizacion = usuario.tipo_organizacion === 'empresa' ? usuario.id_organizacion : undefined

      if (tipoModal === 'crear') {
        const nuevoUsuario = await usuariosService.crearUsuario({
          nombre_usuario: usuario.nombre,
          apellido: usuario.apellido,
          correo: usuario.email,
          telefono: usuario.telefono,
          empresa: usuario.empresa,
          rol: rolPayload,
          password: (usuario as any).password || 'Temporal123!',
          id_institucion: idInstitucion,
          id_organizacion: idOrganizacion,
          rol_organizacion: usuario.rol_organizacion,
        })
        setUsuarios(prev => [...prev, nuevoUsuario])
      } else if (tipoModal === 'editar') {
        const usuarioActualizado = await usuariosService.actualizarUsuario(usuario.id, {
          nombre_usuario: usuario.nombre,
          apellido: usuario.apellido,
          correo: usuario.email,
          telefono: usuario.telefono,
          empresa: usuario.empresa,
          rol: rolPayload,
          activo: usuario.estado === 'activo',
          plan_membresia: usuario.suscripcion === 'premium' ? 'premium' :
                         usuario.suscripcion === 'empresarial' ? 'empresarial' : 'basica',
          id_institucion: idInstitucion,
          id_organizacion: idOrganizacion,
          rol_organizacion: usuario.rol_organizacion,
        })
        setUsuarios(prev => prev.map(u => u.id === usuario.id ? usuarioActualizado : u))
      }
    } catch (error) {
      console.error('Error al guardar usuario:', error)
      const presentacion = resolverPresentacionErrorApi(error, {
        tituloFallback: 'Error',
        mensajeFallback: 'Error al guardar el usuario. Por favor, inténtalo de nuevo.',
        titulosPorCodigo: {
          validation_error: 'Datos inválidos',
          forbidden: 'Acceso denegado',
          conflict: 'Usuario duplicado',
          not_found: 'Usuario no encontrado',
        },
        mensajesPorCodigo: {
          validation_error: 'Verifica la información capturada antes de guardar el usuario.',
          forbidden: 'No tienes permisos para crear o editar usuarios.',
          not_found: 'El usuario que intentas actualizar ya no existe.',
        },
      })
      showToast({
        type: 'error',
        title: presentacion.title,
        message: presentacion.message,
      })
    }
  }

  const manejarEliminarUsuario = async (id: string) => {
    try {
      await usuariosService.eliminarUsuario(id)
      setUsuarios(prev => prev.filter(u => u.id !== id))
    } catch (error) {
      console.error('Error al eliminar usuario:', error)
      const presentacion = resolverPresentacionErrorApi(error, {
        tituloFallback: 'Error',
        mensajeFallback: 'Error al eliminar el usuario. Por favor, inténtalo de nuevo.',
        titulosPorCodigo: {
          forbidden: 'Acceso denegado',
          not_found: 'Usuario no encontrado',
        },
        mensajesPorCodigo: {
          forbidden: 'No tienes permisos para eliminar usuarios.',
          not_found: 'El usuario que intentas eliminar ya no existe.',
        },
      })
      showToast({
        type: 'error',
        title: presentacion.title,
        message: presentacion.message,
      })
    }
  }

  const manejarResetearPassword = async (usuario: Usuario) => {
    if (!window.confirm(`¿Resetear la contraseña de ${usuario.nombre} ${usuario.apellido || ''}? Se generará una clave temporal.`)) {
      return
    }

    try {
      const respuesta = await usuariosService.resetearPassword(usuario.id)
      setPasswordTemporalInfo({
        usuario: `${usuario.nombre} ${usuario.apellido || ''}`.trim(),
        email: usuario.email,
        password: respuesta.password_temporal,
        sesionesInvalidadas: Boolean(respuesta.sesiones_invalidadas),
      })
      showToast({
        type: 'success',
        title: 'Contraseña reseteada',
        message: respuesta.sesiones_invalidadas
          ? 'Se generó una contraseña temporal y se invalidaron las sesiones activas.'
          : 'Se generó una contraseña temporal para el usuario.',
      })
    } catch (error) {
      console.error('Error al resetear contraseña:', error)
      const presentacion = resolverPresentacionErrorApi(error, {
        tituloFallback: 'Error al resetear contraseña',
        mensajeFallback: 'No se pudo generar una contraseña temporal para el usuario.',
        titulosPorCodigo: {
          forbidden: 'Acceso denegado',
          not_found: 'Usuario no encontrado',
        },
        mensajesPorCodigo: {
          forbidden: 'No tienes permisos para resetear contraseñas.',
          not_found: 'El usuario que intentas resetear ya no existe.',
        },
      })
      showToast({
        type: 'error',
        title: presentacion.title,
        message: presentacion.message,
      })
    }
  }

  const copiarPasswordTemporal = async () => {
    if (!passwordTemporalInfo?.password) return
    try {
      await navigator.clipboard.writeText(passwordTemporalInfo.password)
      showToast({
        type: 'success',
        title: 'Contraseña copiada',
        message: 'La contraseña temporal se copió al portapapeles.',
      })
    } catch (error) {
      showToast({
        type: 'error',
        title: 'No se pudo copiar',
        message: 'Copia manualmente la contraseña temporal mostrada.',
      })
    }
  }

  if (cargando) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0a0a0a]">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white/70 dark:bg-black/50 backdrop-blur-xl border-b border-slate-200/50 dark:border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-extrabold bg-gradient-to-r from-slate-900 to-slate-500 bg-clip-text text-transparent dark:from-white dark:to-zinc-400 tracking-tight">
                Gestión de Usuarios
              </h1>
              <p className="mt-1 text-sm font-medium text-slate-500 dark:text-zinc-400">
                Administra usuarios, roles y permisos del sistema con control total
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setMostrarFiltros(!mostrarFiltros)}
                className="group relative inline-flex items-center gap-2 overflow-hidden rounded-xl border border-slate-200/50 bg-white/50 px-5 py-2.5 text-sm font-bold text-slate-700 shadow-sm backdrop-blur-sm transition-all hover:border-slate-300 hover:bg-white dark:border-white/10 dark:bg-white/5 dark:text-zinc-300 dark:hover:bg-white/10"
              >
                <FunnelIcon className="h-4 w-4 transition-transform group-hover:scale-110" />
                Filtros
              </button>
              {tienePermiso('usuarios:crear') && (
                <button
                  onClick={() => abrirModal('crear')}
                  className="group relative inline-flex items-center gap-2 overflow-hidden rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-cyan-500/25 transition-all hover:scale-105 hover:shadow-cyan-500/40"
                >
                  <PlusIcon className="h-4 w-4 transition-transform group-hover:rotate-90" />
                  Nuevo Usuario
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {passwordTemporalInfo && (
          <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/40 dark:bg-amber-900/20">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <p className="text-sm font-semibold text-amber-900 dark:text-amber-100">Contraseña temporal generada</p>
                <p className="mt-1 text-sm text-amber-800 dark:text-amber-200">
                  Usuario: {passwordTemporalInfo.usuario} ({passwordTemporalInfo.email})
                </p>
                <p className="mt-2 font-mono text-lg text-amber-950 dark:text-amber-50">{passwordTemporalInfo.password}</p>
                {passwordTemporalInfo.sesionesInvalidadas && (
                  <p className="mt-2 text-sm text-amber-800 dark:text-amber-200">
                    Las sesiones activas previas del usuario quedaron invalidadas.
                  </p>
                )}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={copiarPasswordTemporal}
                  aria-label="Copiar contraseña temporal"
                  className="rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700"
                >
                  Copiar
                </button>
                <button
                  onClick={() => setPasswordTemporalInfo(null)}
                  aria-label="Cerrar aviso de contraseña temporal"
                  className="rounded-lg border border-amber-300 px-4 py-2 text-sm font-medium text-amber-900 hover:bg-amber-100 dark:border-amber-700 dark:text-amber-100 dark:hover:bg-amber-900/30"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Filtros */}
        <AnimatePresence>
          {mostrarFiltros && (
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.98 }}
              transition={{ duration: 0.2 }}
              className="rounded-2xl border border-slate-200/50 bg-white/70 p-6 shadow-lg backdrop-blur-xl dark:border-white/10 dark:bg-white/5"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                <div>
                  <label htmlFor="usuarios-filtro-busqueda" className="block text-xs font-bold uppercase tracking-widest text-slate-700 dark:text-zinc-300 mb-2">
                    Buscar
                  </label>
                  <div className="relative">
                    <MagnifyingGlassIcon className="h-5 w-5 absolute left-3.5 top-1/2 transform -translate-y-1/2 text-slate-400" />
                    <input
                      id="usuarios-filtro-busqueda"
                      name="busqueda"
                      type="text"
                      placeholder="Nombre, email, empresa..."
                      value={filtros.busqueda}
                      onChange={(e) => setFiltros(prev => ({ ...prev, busqueda: e.target.value }))}
                      className="w-full rounded-xl border border-slate-200/50 bg-white/50 py-2.5 pl-11 pr-4 text-sm text-slate-900 placeholder-slate-400 backdrop-blur-sm transition-all focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500 dark:border-white/10 dark:bg-black/50 dark:text-white dark:placeholder-zinc-600"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="usuarios-filtro-rol" className="block text-xs font-bold uppercase tracking-widest text-slate-700 dark:text-zinc-300 mb-2">
                    Rol
                  </label>
                  <select
                    id="usuarios-filtro-rol"
                    name="rol"
                    value={filtros.rol}
                    onChange={(e) => setFiltros(prev => ({ ...prev, rol: e.target.value }))}
                    className="w-full rounded-xl border border-slate-200/50 bg-white/50 px-4 py-2.5 text-sm text-slate-900 backdrop-blur-sm transition-all focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500 dark:border-white/10 dark:bg-black/50 dark:text-white"
                  >
                    <option value="">Todos los roles</option>
                    <option value="global:superadmin">Global: Superadmin</option>
                    <option value="global:admin">Global: Administrador</option>
                    <option value="global:usuario">Global: Usuario</option>
                    <option value="institucion:admin">Institución: Admin</option>
                    <option value="institucion:supervisor">Institución: Supervisor</option>
                    <option value="institucion:jefe">Institución: Jefe</option>
                    <option value="institucion:usuario">Institución: Usuario</option>
                    <option value="institucion:maestro">Institución: Maestro</option>
                    <option value="institucion:alumno">Institución: Alumno</option>
                    <option value="empresa:admin">Empresa: Admin</option>
                    <option value="empresa:moderador">Empresa: Moderador</option>
                    <option value="empresa:supervisor">Empresa: Supervisor</option>
                    <option value="empresa:contador">Empresa: Contador</option>
                    <option value="empresa:usuario">Empresa: Usuario</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="usuarios-filtro-estado" className="block text-xs font-bold uppercase tracking-widest text-slate-700 dark:text-zinc-300 mb-2">
                    Estado
                  </label>
                  <select
                    id="usuarios-filtro-estado"
                    name="estado"
                    value={filtros.estado}
                    onChange={(e) => setFiltros(prev => ({ ...prev, estado: e.target.value }))}
                    className="w-full rounded-xl border border-slate-200/50 bg-white/50 px-4 py-2.5 text-sm text-slate-900 backdrop-blur-sm transition-all focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500 dark:border-white/10 dark:bg-black/50 dark:text-white"
                  >
                    <option value="">Todos los estados</option>
                    <option value="activo">Activo</option>
                    <option value="inactivo">Inactivo</option>
                    <option value="suspendido">Suspendido</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="usuarios-filtro-suscripcion" className="block text-xs font-bold uppercase tracking-widest text-slate-700 dark:text-zinc-300 mb-2">
                    Suscripción
                  </label>
                  <select
                    id="usuarios-filtro-suscripcion"
                    name="suscripcion"
                    value={filtros.suscripcion}
                    onChange={(e) => setFiltros(prev => ({ ...prev, suscripcion: e.target.value }))}
                    className="w-full rounded-xl border border-slate-200/50 bg-white/50 px-4 py-2.5 text-sm text-slate-900 backdrop-blur-sm transition-all focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500 dark:border-white/10 dark:bg-black/50 dark:text-white"
                  >
                    <option value="">Todas las suscripciones</option>
                    <option value="basica">Básica</option>
                    <option value="premium">Premium</option>
                    <option value="empresarial">Empresarial</option>
                    <option value="institucional">Institucional</option>
                  </select>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Estadísticas rápidas */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-7 gap-4 lg:gap-5 mb-8">
          {[
            { 
              titulo: 'Total Usuarios', 
              valor: usuarios.length, 
              icono: UsersIcon, 
              color: 'from-blue-500 to-cyan-500', 
              shadow: 'shadow-blue-500/20' 
            },
            { 
              titulo: 'Usuarios Activos', 
              valor: usuarios.filter(u => u.estado === 'activo').length, 
              icono: CheckCircleIcon, 
              color: 'from-emerald-400 to-green-600', 
              shadow: 'shadow-emerald-500/20' 
            },
            { 
              titulo: 'Administradores', 
              valor: totalAdminsGlobales, 
              icono: ShieldCheckIcon, 
              color: 'from-fuchsia-500 to-purple-600', 
              shadow: 'shadow-fuchsia-500/20' 
            },
            { 
              titulo: 'Suspendidos', 
              valor: usuarios.filter(u => u.estado === 'suspendido').length, 
              icono: ExclamationTriangleIcon, 
              color: 'from-amber-400 to-orange-500', 
              shadow: 'shadow-amber-500/20' 
            },
            { 
              titulo: 'Globales', 
              valor: totalGlobales, 
              icono: UserIcon, 
              color: 'from-indigo-400 to-blue-600', 
              shadow: 'shadow-indigo-500/20' 
            },
            { 
              titulo: 'Institucionales', 
              valor: totalInstitucionales, 
              icono: ShieldCheckIcon, 
              color: 'from-rose-400 to-red-600', 
              shadow: 'shadow-rose-500/20' 
            },
            { 
              titulo: 'Empresariales', 
              valor: totalEmpresariales, 
              icono: UserIcon, 
              color: 'from-sky-400 to-cyan-600', 
              shadow: 'shadow-sky-500/20' 
            },
          ].map((stat, i) => (
            <div key={i} className="group relative overflow-hidden rounded-2xl border border-slate-200/50 bg-white/70 p-5 shadow-sm backdrop-blur-xl transition-all duration-300 hover:-translate-y-1 hover:shadow-xl dark:border-white/10 dark:bg-white/5">
              <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-gradient-to-br opacity-20 blur-2xl transition-all group-hover:scale-150 group-hover:opacity-30 dark:opacity-10 dark:group-hover:opacity-20"></div>
              <div className="flex flex-col gap-3 relative z-10">
                <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${stat.color} text-white shadow-lg ${stat.shadow}`}>
                  <stat.icono className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-zinc-400 leading-tight">{stat.titulo}</p>
                  <p className="text-2xl font-black text-slate-900 dark:text-white tracking-tight leading-none mt-1">{stat.valor}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {estadisticasUsuarios && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 mb-8">
            <div className="rounded-2xl border border-slate-200/50 bg-white/70 p-6 shadow-sm backdrop-blur-xl dark:border-white/10 dark:bg-white/5">
              <h3 className="text-sm font-bold uppercase tracking-widest text-slate-900 dark:text-white mb-5 flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.8)]"></span> Roles Globales
              </h3>
              <div className="space-y-4">
                {Object.entries(estadisticasUsuarios.porRolGlobal).map(([rol, cantidad]) => (
                  <div key={rol} className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-slate-600 dark:text-zinc-400 capitalize">{rol}</span>
                    <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-slate-100 text-xs font-bold text-slate-900 dark:bg-white/10 dark:text-white">{cantidad}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200/50 bg-white/70 p-6 shadow-sm backdrop-blur-xl dark:border-white/10 dark:bg-white/5">
              <h3 className="text-sm font-bold uppercase tracking-widest text-slate-900 dark:text-white mb-5 flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-rose-500 shadow-[0_0_8px_rgba(244,63,94,0.8)]"></span> Roles Institucionales
              </h3>
              <div className="space-y-4">
                {Object.entries(estadisticasUsuarios.porRolOrganizacion.institucion).length ? Object.entries(estadisticasUsuarios.porRolOrganizacion.institucion).map(([rol, cantidad]) => (
                  <div key={rol} className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-slate-600 dark:text-zinc-400 capitalize">{rol}</span>
                    <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-slate-100 text-xs font-bold text-slate-900 dark:bg-white/10 dark:text-white">{cantidad}</span>
                  </div>
                )) : <p className="text-sm font-medium text-slate-400 dark:text-zinc-500">Sin miembros institucionales.</p>}
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200/50 bg-white/70 p-6 shadow-sm backdrop-blur-xl dark:border-white/10 dark:bg-white/5">
              <h3 className="text-sm font-bold uppercase tracking-widest text-slate-900 dark:text-white mb-5 flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-sky-500 shadow-[0_0_8px_rgba(14,165,233,0.8)]"></span> Roles Empresariales
              </h3>
              <div className="space-y-4">
                {Object.entries(estadisticasUsuarios.porRolOrganizacion.empresa).length ? Object.entries(estadisticasUsuarios.porRolOrganizacion.empresa).map(([rol, cantidad]) => (
                  <div key={rol} className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-slate-600 dark:text-zinc-400 capitalize">{rol}</span>
                    <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-slate-100 text-xs font-bold text-slate-900 dark:bg-white/10 dark:text-white">{cantidad}</span>
                  </div>
                )) : <p className="text-sm font-medium text-slate-400 dark:text-zinc-500">Sin miembros empresariales.</p>}
              </div>
            </div>
          </div>
        )}

        {/* Tabla de usuarios */}
        <div className="rounded-2xl border border-slate-200/50 bg-white/70 shadow-xl backdrop-blur-xl dark:border-white/10 dark:bg-white/5 overflow-hidden">
          <div className="px-6 py-5 border-b border-slate-200/50 dark:border-white/10">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
              <h2 className="text-xl font-extrabold text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
                <UsersIcon className="h-5 w-5 text-cyan-500" /> Directorio ({usuariosFiltrados.length})
              </h2>
              <div className="flex items-center gap-2 rounded-full bg-slate-100 px-4 py-1.5 text-xs font-bold text-slate-600 dark:bg-white/10 dark:text-zinc-300">
                <span>Mostrando</span>
                <span className="text-cyan-600 dark:text-cyan-400">{indiceInicio + 1}-{Math.min(indiceFin, usuariosFiltrados.length)}</span>
                <span>de</span>
                <span className="text-cyan-600 dark:text-cyan-400">{usuariosFiltrados.length}</span>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200/50 dark:divide-white/10">
              <thead className="bg-slate-50/50 dark:bg-white/[0.02]">
                <tr>
                  <th
                    onClick={() => manejarOrdenamiento('nombre')}
                    className="px-6 py-4 text-left text-xs font-bold text-slate-500 dark:text-zinc-400 uppercase tracking-wider cursor-pointer hover:bg-slate-100/50 dark:hover:bg-white/5 transition-colors"
                  >
                    <div className="flex items-center space-x-2">
                      <span>Usuario</span>
                      {ordenamiento.campo === 'nombre' && (
                        ordenamiento.direccion === 'asc' ? 
                        <ArrowUpIcon className="h-3.5 w-3.5 text-cyan-500" /> : 
                        <ArrowDownIcon className="h-3.5 w-3.5 text-cyan-500" />
                      )}
                    </div>
                  </th>
                  <th
                    onClick={() => manejarOrdenamiento('rol')}
                    className="px-6 py-4 text-left text-xs font-bold text-slate-500 dark:text-zinc-400 uppercase tracking-wider cursor-pointer hover:bg-slate-100/50 dark:hover:bg-white/5 transition-colors"
                  >
                    <div className="flex items-center space-x-2">
                      <span>Rol</span>
                      {ordenamiento.campo === 'rol' && (
                        ordenamiento.direccion === 'asc' ? 
                        <ArrowUpIcon className="h-3.5 w-3.5 text-cyan-500" /> : 
                        <ArrowDownIcon className="h-3.5 w-3.5 text-cyan-500" />
                      )}
                    </div>
                  </th>
                  <th
                    onClick={() => manejarOrdenamiento('estado')}
                    className="px-6 py-4 text-left text-xs font-bold text-slate-500 dark:text-zinc-400 uppercase tracking-wider cursor-pointer hover:bg-slate-100/50 dark:hover:bg-white/5 transition-colors"
                  >
                    <div className="flex items-center space-x-2">
                      <span>Estado</span>
                      {ordenamiento.campo === 'estado' && (
                        ordenamiento.direccion === 'asc' ? 
                        <ArrowUpIcon className="h-3.5 w-3.5 text-cyan-500" /> : 
                        <ArrowDownIcon className="h-3.5 w-3.5 text-cyan-500" />
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-slate-500 dark:text-zinc-400 uppercase tracking-wider">
                    Suscripción
                  </th>
                  <th
                    onClick={() => manejarOrdenamiento('fechaRegistro')}
                    className="px-6 py-4 text-left text-xs font-bold text-slate-500 dark:text-zinc-400 uppercase tracking-wider cursor-pointer hover:bg-slate-100/50 dark:hover:bg-white/5 transition-colors"
                  >
                    <div className="flex items-center space-x-2">
                      <span>Registro</span>
                      {ordenamiento.campo === 'fechaRegistro' && (
                        ordenamiento.direccion === 'asc' ? 
                        <ArrowUpIcon className="h-3.5 w-3.5 text-cyan-500" /> : 
                        <ArrowDownIcon className="h-3.5 w-3.5 text-cyan-500" />
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-slate-500 dark:text-zinc-400 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200/50 dark:divide-white/10">
                <AnimatePresence>
                  {usuariosPaginados.map((usuario, index) => (
                    <motion.tr
                      key={usuario.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ delay: index * 0.05 }}
                      className="group hover:bg-slate-50/50 dark:hover:bg-white/[0.02] transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            {usuario.avatar ? (
                              <img className="h-10 w-10 rounded-full" src={usuario.avatar} alt="" />
                            ) : (
                              <div className="h-10 w-10 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center">
                                <UserIcon className="h-6 w-6 text-gray-500 dark:text-gray-400" />
                              </div>
                            )}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {usuario.nombre} {usuario.apellido}
                              {usuario.verificado && (
                                <CheckCircleIcon className="h-4 w-4 text-green-500 inline ml-1" />
                              )}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">{usuario.email}</div>
                            {usuario.empresa && (
                              <div className="text-xs text-gray-400 dark:text-gray-500">{usuario.empresa}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${obtenerColorRol(usuario.rol)}`}>
                          {usuariosService.formatearEtiquetaRol(usuario)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${obtenerColorEstado(usuario.estado)}`}>
                          {usuario.estado.charAt(0).toUpperCase() + usuario.estado.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {usuario.suscripcion && (
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${obtenerColorSuscripcion(usuario.suscripcion)}`}>
                            {usuario.suscripcion.charAt(0).toUpperCase() + usuario.suscripcion.slice(1)}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        <div>{new Date(usuario.fechaRegistro).toLocaleDateString('es-ES')}</div>
                        {usuario.ultimoAcceso && (
                          <div className="text-xs text-gray-400">
                            Último: {new Date(usuario.ultimoAcceso).toLocaleDateString('es-ES')}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => abrirModal('ver', usuario)}
                              aria-label={`Ver detalles de ${usuario.nombre} ${usuario.apellido || ''}`.trim()}
                            className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 bg-blue-50 dark:bg-blue-900/20 p-2 rounded-lg transition-colors duration-200"
                            title="Ver detalles"
                          >
                            <EyeIcon className="h-5 w-5" />
                          </button>
                          {tienePermiso('usuarios:editar') && (
                            <button
                              onClick={() => abrirModal('editar', usuario)}
                                aria-label={`Editar usuario ${usuario.nombre} ${usuario.apellido || ''}`.trim()}
                              className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300 bg-green-50 dark:bg-green-900/20 p-2 rounded-lg transition-colors duration-200"
                              title="Editar usuario"
                            >
                              <PencilIcon className="h-5 w-5" />
                            </button>
                          )}
                          {tienePermiso('usuarios:editar') && (
                            <button
                              onClick={() => manejarResetearPassword(usuario)}
                                aria-label={`Resetear contraseña de ${usuario.nombre} ${usuario.apellido || ''}`.trim()}
                              className="text-amber-600 hover:text-amber-900 dark:text-amber-400 dark:hover:text-amber-300 bg-amber-50 dark:bg-amber-900/20 p-2 rounded-lg transition-colors duration-200"
                              title="Resetear contraseña"
                            >
                              <KeyIcon className="h-5 w-5" />
                            </button>
                          )}
                          {tienePermiso('usuarios:eliminar') && (
                            <button
                              onClick={() => {
                                if (window.confirm('¿Estás seguro de que deseas eliminar este usuario?')) {
                                  manejarEliminarUsuario(usuario.id)
                                }
                              }}
                                aria-label={`Eliminar usuario ${usuario.nombre} ${usuario.apellido || ''}`.trim()}
                              className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 bg-red-50 dark:bg-red-900/20 p-2 rounded-lg transition-colors duration-200"
                              title="Eliminar usuario"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>

          {/* Paginación */}
          {totalPaginas > 1 && (
            <div className="px-6 py-4 border-t border-slate-200/50 dark:border-white/10 bg-slate-50/30 dark:bg-white/[0.01]">
              <div className="flex items-center justify-between">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setPaginaActual(prev => Math.max(prev - 1, 1))}
                    disabled={paginaActual === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => setPaginaActual(prev => Math.min(prev + 1, totalPaginas))}
                    disabled={paginaActual === totalPaginas}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Siguiente
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      Mostrando <span className="font-medium">{indiceInicio + 1}</span> a{' '}
                      <span className="font-medium">{Math.min(indiceFin, usuariosFiltrados.length)}</span> de{' '}
                      <span className="font-medium">{usuariosFiltrados.length}</span> resultados
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => setPaginaActual(prev => Math.max(prev - 1, 1))}
                        disabled={paginaActual === 1}
                        aria-label="Página anterior"
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronLeftIcon className="h-5 w-5" />
                      </button>
                      {Array.from({ length: totalPaginas }, (_, i) => i + 1).map((pagina) => (
                        <button
                          key={pagina}
                          onClick={() => setPaginaActual(pagina)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            pagina === paginaActual
                              ? 'z-10 bg-blue-50 dark:bg-blue-900 border-blue-500 text-blue-600 dark:text-blue-400'
                              : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600'
                          }`}
                        >
                          {pagina}
                        </button>
                      ))}
                      <button
                        onClick={() => setPaginaActual(prev => Math.min(prev + 1, totalPaginas))}
                        disabled={paginaActual === totalPaginas}
                        aria-label="Página siguiente"
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronRightIcon className="h-5 w-5" />
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal de usuario */}
      <ModalUsuario
        isOpen={modalVisible}
        onClose={cerrarModal}
        tipo={tipoModal}
        usuario={usuarioSeleccionado}
        onGuardar={manejarGuardarUsuario}
        onEliminar={manejarEliminarUsuario}
      />
    </div>
  )
}

export default PaginaGestionUsuarios
